/**
* AppInstaller.js
* 
* Created 2021-03
*
* @author reinier@ranj.nl
**/

import SoundManager from "../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import TaskFactory from '../../../../lib/html5-software-library/src/ranj/util/executor/TaskFactory.js';

//Import asset file(s) used by this module.
import '../../../assets/skins/LanguageSelectAssets.js';
import '../../../assets/skins/SplashAssets.js';

import GoogleAnalyticsInstaller from '../../analytics/GoogleAnalyticsInstaller.js';
import AppSpecificInstaller from "../../app/AppSpecificInstaller";
import LayoutInstaller from '../../layout/LayoutInstaller.js';
import NGTInstaller from '../../ngt/base/NGTInstaller.js';
import AssetLibrary from '../assetloading/AssetLibrary';
import AssetLoadService from '../assetloading/AssetLoadService.js';
import PromptRetryLoadingAssetsCommand from "../assetloading/controller/PromptRetryLoadingAssetsCommand";
import AssetLoadServiceEvent from "../assetloading/events/AssetLoadServiceEvent";
import Config from '../config/Config.js';
import FontsInstaller from "../fonts/context/FontsInstaller";
import AddLanguagePackCommand from "../language/controller/AddLanguagePackCommand";
import InitializeLanguageConfigCommand from "../language/controller/InitializeLanguageConfigCommand";
import LanguagePackLoadedEvent from "../language/controller/LanguagePackLoadedEvent";
import PromptLanguageSelectionCommand from "../language/controller/PromptLanguageSelectionCommand";
import SelectLanguageCommand from "../language/controller/SelectLanguageCommand";
import LanguageEvent from "../language/model/LanguageEvent";
import LanguageModel from "../language/model/LanguageModel";
import LanguageSelectScreenMediator from "../language/view/LanguageSelectScreenMediator";
import ScreenNavigationInstaller from '../navigation/ScreenNavigationInstaller.js';
import SplashLoadScreenMediator from "../splash/SplashLoadScreenMediator";
import Startup from '../startup/Startup.js';
import StartupCommand from '../startup/StartupCommand.js';
import SystemMessageInstaller from '../system-message/SystemMessageInstaller.js';
import ClickEaterFactory from '../ui/ClickEaterFactory.js';
import SkinKeyRegistry from '../ui/SkinKeyRegistry.js';
import AbstractInstaller from './AbstractInstaller.js';
import AppDebugInstaller from './AppDebugInstaller.js';
import ContextEvent from './ContextEvent';
import DestroyCommand from './DestroyCommand.js';
import MediatorManager from './MediatorManager';
import MediatorMapping from './MediatorMapping';


// Start of scaffolded imports (don't remove "#ImportPointer")
// #ImportPointer
// End of scaffolded imports

export default class AppInstaller extends AbstractInstaller {

    /**
     * @param {infuse.Injector} injector 
     * @param {soma.Commands} commands 
     * @param {MediatorMapping} mediatorMap 
     */
    constructor(injector, commands, mediatorMap) {
        super(injector, commands, mediatorMap);

        // ---------------------
        //      Generic
        // ---------------------

        injector.mapClass("languagePack", RevisableLanguagePack, true);
        injector.mapClass("assetLibrary", AssetLibrary, true);
        injector.mapClass("assetLoadService", AssetLoadService, true);
        injector.mapClass("languageModel", LanguageModel, true);
        // Make sure an instance of the mediator manager exists, so screen navigation events are monitored.
        const mediatorManager = injector.createInstance(MediatorManager);
        injector.mapValue("mediatorManager", mediatorManager);
        injector.mapClass("config", Config, true);
        injector.mapClass("clickEaterFactory", ClickEaterFactory, true);

        commands.add(ContextEvent.STARTUP, StartupCommand);
        commands.add(ContextEvent.DESTROY, DestroyCommand);
        commands.add(LanguagePackLoadedEvent.LOADED, AddLanguagePackCommand);
        commands.add(AssetLoadServiceEvent.LOAD_COMPLETE_WITH_ERRORS, PromptRetryLoadingAssetsCommand);
        commands.add(LanguageEvent.INITIALIZE, InitializeLanguageConfigCommand);
        commands.add(LanguageEvent.PROMPT, PromptLanguageSelectionCommand);
        commands.add(LanguageEvent.SELECT, SelectLanguageCommand);

        // Set up lifecycle objects to which installer can add 
        // See corresponding convenience methods in AbstractInstaller
        injector.mapClass("taskFactory", TaskFactory, true);
        injector.mapClass("startup", Startup, true);

        // Sound
        injector.mapClass("soundManager", SoundManager, true);

        // Screen navigation  
        this._install(ScreenNavigationInstaller);

        // Debug tools
        this._install(AppDebugInstaller);

        // -------------------------------
        //      Project specific
        // -------------------------------

        SkinKeyRegistry.LANGUAGE_SELECT_SCREEN_SKIN = this._mapMediator(LanguageSelectScreenMediator, lib.LanguageSelectAssets.LanguageSelectScreenSkin);
        SkinKeyRegistry.SPLASH_LOAD_SCREEN_SKIN = this._mapMediator(SplashLoadScreenMediator, lib.SplashAssets.SplashLoadScreenSkin);

        this._addAssetsToConfigurationManifest(
            { id: "assets", src: "assets/assets.json" },
            { id: "config", src: "assets/config.json" },
            { id: "languages", src: "assets/languages.json" },
        );

        this._addAssetGroupsToList(AppInstaller.PRELOAD_ASSET_GROUPS, "LanguageSelectAssets", "SplashAssets",  "sounds");
        this._addAssetGroupsToList(AppInstaller.STARTUP_ASSET_GROUPS, "common", "MainAssets");

        // install project specific modules
        this._install(FontsInstaller);
        this._install(LayoutInstaller);
        this._install(NGTInstaller);
        this._install(GoogleAnalyticsInstaller);
        this._install(SystemMessageInstaller);
        this._install(AppSpecificInstaller);


        // Start of scaffolded elements (don't remove "#Pointer")
        // #Pointer
        // End of scaffolded elements
    };

}

/**
 * Id of asset group list used for preloading (before splash screen is shown). 
 * This typically contains the visual assets and language file for the splash screen. 
 */
AppInstaller.PRELOAD_ASSET_GROUPS = "preloadAssetGroups";

/**
 * Id of asset group list that will be loaded during startup (while splash screen is shown). 
 * This typically contains the main visual assets, sounds, languagepacks etc
 */
AppInstaller.STARTUP_ASSET_GROUPS = "startupAssetGroups";